import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ApiDigitalAuthAuthentication } from '../../services/auth/authentication.service';
import { Error } from '../../models/error.model';
import { Token } from '../../models/token.model';
import { GlobalSwal } from '../../styles/swal.style';
import { legendIconInvalid } from '../../utils/legend.data.util';
import { authApiKey, authClosed, authEmail, authExpired, authLogin } from '../../scripts/authentication.script';
import { evaluateLegendRequiredIcon } from '../../scripts/validate.legend.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppLegend from '../../components/element/Legend';

export interface LoginPageProps {};

let errorResponse: Error, tokenResponse: Token;

const LoginPage: React.FunctionComponent<LoginPageProps> = props => {
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const initialEmail = useMemo(() => { try { return authEmail() } catch { return '' } }, [])
  const [email, setEmail] = useState({value: initialEmail, valid: initialEmail.length > 0})
  const [password, setPassword] = useState({value: '', valid: false})

  const submitLogin = async (event: React.ChangeEvent <HTMLFormElement>) => {
    event.preventDefault()
    setLoadIndicator('on')

    if (email.valid && password.valid) {
      await ApiDigitalAuthAuthentication.login(authApiKey(), email.value, password.value).then( (response) => {
        if (response.status === 200) {
          tokenResponse = response.data
          console.log('[ORDER 1 — Login] response.data=', JSON.stringify(tokenResponse?.companies), '| company=', JSON.stringify(tokenResponse?.company), '| has_multiple_companies=', tokenResponse?.has_multiple_companies)
          authLogin(tokenResponse)

          const hasCompanies = Array.isArray(tokenResponse?.companies) && tokenResponse.companies.length > 0
          console.log('[ORDER 2 — Login] hasCompanies=', hasCompanies, '| navigating to=', hasCompanies ? '/main/dashboard' : '/main/welcome')

          GlobalSwal.fire({
            title: lang.labels.successfullyLoggedIn,
            icon: 'success',
            showConfirmButton: false,
            timer: 1500
          }).then( () => {
            console.log('[ORDER 3 — Login] navigate NOW to', hasCompanies ? '/main/dashboard' : '/main/welcome')
            navigate(hasCompanies ? '/main/dashboard' : '/main/welcome', {replace: true})
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (email.value) legendIconInvalid('input-email', 'legend-email-required')
        if (password.value) legendIconInvalid('input-password', 'legend-password-required')
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeEmail = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setEmail({...email, value: event.target.value})
  }

  const handleChangePassword = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPassword({...password , value: event.target.value})
  }

  const validateEmail = () => {
    evaluateLegendRequiredIcon(expressions.email, email, setEmail, 'input-email', 'legend-email-valid', 'legend-email-required')
  }

  const validatePassword = () => {
    evaluateLegendRequiredIcon(expressions.limit, password, setPassword, 'input-password', 'legend-password-valid', 'legend-password-required')
  }

  useEffect(() => {
    try {
      if (authExpired()) {
        if (!toast.isActive('warn')) {
          toast.warn(lang.labels?.yourSessionHasExpired ?? '', {toastId: 'warn'})
        } else {
          toast.update('warn', {render: lang.labels?.yourSessionHasExpired ?? ''})
        }
      }
      if (authClosed()) {
        if (!toast.isActive('error')) {
          toast.error(lang.labels?.yourSessionHasBeenClosed ?? '', {toastId: 'error'})
        } else {
          toast.update('error', {render: lang.labels?.yourSessionHasBeenClosed ?? ''})
        }
      }
    } catch (e) {
      console.error('Login session check failed:', e)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang.code])

  return (
    <div className="w-lg-500px bg-body rounded shadow-sm p-10 mx-auto">
      <form className="form w-100" noValidate onSubmit={submitLogin}>
        <div className="text-center mb-10">
          <h1 className="text-dark text-capitalize mb-3">{lang.labels.signIn}</h1>
          <div className="text-muted fw-bold fs-4">
            {lang.labels.newHere}
            <Link to="/auth/register" className="link-primary fw-bolder ms-2">{lang.labels.createAnAccount}</Link>
          </div>
        </div>
        <div className="fv-row mb-5">
          <label className="form-label fs-6 fw-bolder text-dark">{lang.labels.email}</label>
          <input id="input-email" className="form-control form-control" type="email" name="email" autoComplete="username" value={email.value} onChange={handleChangeEmail} onKeyUp={validateEmail} onBlur={validateEmail} />
          <AppLegend attribute={{check: email.valid, name: "email", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
        </div>
        <div className="fv-row mb-10">
          <div className="d-flex flex-stack mb-2">
            <label className="form-label fs-6 fw-bolder text-dark">{lang.labels.password}</label>
            <Link to="/auth/recover" className="link-primary fs-6 fw-bolder">{lang.labels.forgotPassword}</Link>
          </div>
          <input id="input-password" className="form-control form-control-lg" type="password" name="password" autoComplete="current-password" value={password.value} onChange={handleChangePassword} onKeyUp={validatePassword} onBlur={validatePassword} />
          <AppLegend attribute={{check: password.valid, name: "password", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
        </div>
        <div className="text-center">
          <button className="btn btn-lg btn-primary w-100" type="submit" data-kt-indicator={loadIndicator}>
            <span className="indicator-label fw-bolder">{lang.labels.continue}</span>
            <span className="indicator-progress">
              {lang.labels.pleaseWait}
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          </button>
        </div>
      </form>
    </div>
  )
};

export default LoginPage;
